import "@xyflow/react/dist/style.css";

import { useMemo } from "react";
import { useSelector } from "@xstate/react";
import { type Edge as FlowEdge, Handle, type Node, Position, ReactFlow } from "@xyflow/react";
import { BaseEdge, type Edge, type EdgeProps, getSmoothStepPath } from "@xyflow/react";

import type { PlayerId } from "@/types/branded";
import { theme } from "@/ui/theme";
import { computeRounds } from "../graphUtils";
import type { MatchId } from "../types";
import type { KnockoutGraph } from "../types/graph";
import type { KnockoutNode } from "../types/node";

import type { KnockoutMatchActorRef } from "./knockout.machine";
import { KnockoutContext } from "./KnockoutProvider";

export type KnockoutEdgeData = {
  columnX: number; // The x-coordinate where the edge should bend for vertical alignment
};

export type KnockoutEdgeType = Edge & { data: KnockoutEdgeData };

export const KnockoutEdge = (props: EdgeProps<KnockoutEdgeType>) => {
  const { id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, data, style } =
    props;

  // Where we want ALL edges to bend (vertical column alignment)
  const desiredX = data?.columnX ?? (sourceX + targetX) / 2;

  const dx = targetX - sourceX;

  let stepPosition = dx !== 0 ? (desiredX - sourceX) / dx : 0.5;

  // Prevent weird flipping
  stepPosition = Math.max(0, Math.min(1, stepPosition));

  const [path] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    stepPosition,
    borderRadius: 3,
  });

  return <BaseEdge id={id} path={path} style={style} />;
};

type KnockoutMatchNodeData = {
  node: KnockoutNode;
  actorRef: KnockoutMatchActorRef;
  fallbacks: [string, string];
  matchNumber: number;
  resolvePlayerName?: (pid: PlayerId | null | undefined) => string;
};

const X_SPACING = 300;
const Y_SPACING = 130;
const BRACKET_GAP = 120;

export const buildKnockoutView = (
  graph: KnockoutGraph,
  matchRefs: Map<string, KnockoutMatchActorRef>,
  resolvePlayerName?: (pid: PlayerId | null | undefined) => string,
) => {
  let matchCounter = 0;
  const rounds = computeRounds(graph);
  const nodes: Node<KnockoutMatchNodeData>[] = [];
  const edges: FlowEdge[] = [];
  const fallbacks = new Map<string, [string, string]>();

  graph.nodes.forEach((n) => fallbacks.set(n.id, ["TBD", "TBD"]));

  graph.edges.forEach((e, i) => {
    if (e.kind === "match") {
      const sourceSnapshot = matchRefs.get(e.from)?.getSnapshot();
      const isSourceDone = sourceSnapshot?.status === "done";

      edges.push({
        id: `e-${i}`,
        source: e.from,
        target: e.to,
        type: e.outcome === "winner" ? "knockout" : "bezier",
        data: { columnX: rounds.get(e.to)! * X_SPACING },
        animated: isSourceDone,
        style: {
          stroke: isSourceDone ? theme.colors.primary : theme.colors.border,
          strokeWidth: 2,
          opacity: isSourceDone ? 1 : 0.3,
        },
      });
    }
  });

  const structure = new Map<number, Map<number, string[]>>();
  graph.nodes.forEach((node) => {
    if (!structure.has(node.bracket)) structure.set(node.bracket, new Map());
    const byRound = structure.get(node.bracket)!;
    const r = rounds.get(node.id)!;
    if (!byRound.has(r)) byRound.set(r, []);
    byRound.get(r)!.push(node.id);
  });

  let globalYOffset = 0;
  const sortedBrackets = Array.from(structure.keys()).sort((a, b) => a - b);

  // Keep track of the Y positions we calculate so next rounds can center off them
  const yPositions = new Map<string, number>();

  for (const bracket of sortedBrackets) {
    const roundsMap = structure.get(bracket)!;
    const roundKeys = Array.from(roundsMap.keys()).sort((a, b) => a - b);

    let leafYCounter = globalYOffset;
    let maxBracketY = globalYOffset;
    let isOnlyChampionship = true;

    for (const round of roundKeys) {
      const matches = roundsMap.get(round)!;

      matches.forEach((id) => {
        const node = graph.nodes.get(id as MatchId)!;

        if (!node.isChampionship) isOnlyChampionship = false;

        const xPos = round * X_SPACING;

        // Find incoming match edges to vertically center this node
        const incomingEdges = graph.edges.filter((e) => e.kind === "match" && e.to === id);

        // To prevent cross-bracket overlapping (like Lower Bracket drops aligning
        // with Upper Bracket matches), we only average feeders from the SAME bracket,
        // UNLESS it's a Championship match (which connects brackets).
        const feedersToUse = node.isChampionship
          ? incomingEdges
          : incomingEdges.filter((e) => {
              if (e.kind !== "match") return false;

              const sourceNode = graph.nodes.get(e.from);
              return sourceNode && sourceNode.bracket === node.bracket;
            });

        const incomingYs = feedersToUse
          .filter((e) => e.kind === "match")
          .map((e) => yPositions.get(e.from))
          .filter((y) => y !== undefined);

        let yPos: number;
        if (incomingYs.length > 0) {
          // Vertically center based on the average Y of the feeder matches
          yPos = incomingYs.reduce((sum, val) => sum + val, 0) / incomingYs.length;
        } else {
          // No valid feeders -> assign sequential Y for base rounds/leaves
          yPos = leafYCounter;
          leafYCounter += Y_SPACING;
        }

        yPositions.set(id, yPos);
        maxBracketY = Math.max(maxBracketY, yPos);

        matchCounter++;
        nodes.push({
          id,
          type: "tournamentMatch",
          position: { x: xPos, y: yPos },
          data: {
            node,
            actorRef: matchRefs.get(id)!,
            fallbacks: fallbacks.get(id)!,
            matchNumber: matchCounter,
            resolvePlayerName,
          },
        });
      });
    }

    // Increment the global offset for the next bracket, unless this bracket
    // was exclusively championship matches floating vertically in the middle.
    if (!isOnlyChampionship) {
      globalYOffset = Math.max(leafYCounter, maxBracketY + Y_SPACING) + BRACKET_GAP;
    }
  }

  return { nodes, edges };
};

const resolveDisplay = (
  pid: PlayerId | null | undefined,
  fallback: string,
  resolver?: (pid: PlayerId | null | undefined) => string,
): string => {
  if (resolver) return resolver(pid);
  if (pid === null) return "BYE";
  if (pid === undefined) return fallback;
  return String(pid);
};

const MatchNode = ({ data }: { data: KnockoutMatchNodeData }) => {
  const { node, actorRef, fallbacks, matchNumber, resolvePlayerName } = data;

  // Subscribe to the live state of this specific match
  const snapshot = useSelector(actorRef, (s) => s);
  const { players, winner } = snapshot.context;
  const isDone = snapshot.status === "done";
  const isReady = snapshot.matches("ready");

  const getBorderColor = () => {
    if (node.isChampionship) return theme.colors.gold;
    if (isReady) return theme.colors.primary;
    return theme.colors.border;
  };

  const getStatusLabel = () => {
    if (isDone) return "COMPLETED";
    if (isReady) return "READY";
    return "WAITING";
  };

  const nameA = resolveDisplay(players[0], fallbacks[0], resolvePlayerName);
  const nameB = resolveDisplay(players[1], fallbacks[1], resolvePlayerName);

  return (
    <div
      style={{
        padding: "12px",
        borderRadius: theme.radii.sm,
        background: theme.colors.bgCard,
        border: `1px solid ${getBorderColor()}`,
        height: 105,
        aspectRatio: "13 / 6",
        boxShadow: theme.shadow.card as string,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ fontSize: 10, color: theme.colors.textDim, fontWeight: 700 }}>
          Match {matchNumber}
        </span>
        <span
          style={{
            fontSize: 9,
            color: isReady ? theme.colors.success : theme.colors.textDim,
            fontWeight: 800,
          }}
        >
          {getStatusLabel()}
        </span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
        <PlayerRow
          displayName={nameA}
          isPresent={!!players[0]}
          isWinner={isDone && winner === players[0]}
        />
        <div style={{ height: "1px", background: theme.colors.border, margin: "4px 0" }} />
        <PlayerRow
          displayName={nameB}
          isPresent={!!players[1]}
          isWinner={isDone && winner === players[1]}
        />
      </div>

      <Handle type="target" position={Position.Left} style={{ visibility: "hidden" }} />
      <Handle type="source" position={Position.Right} style={{ visibility: "hidden" }} />
    </div>
  );
};

const PlayerRow = ({
  displayName,
  isPresent,
  isWinner,
}: {
  displayName: string;
  isPresent: boolean;
  isWinner: boolean;
}) => (
  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
    <span style={{ color: isPresent ? theme.colors.text : theme.colors.textDim }}>
      {displayName}
    </span>
    {isWinner && <span style={{ color: theme.colors.gold }}>🏆</span>}
  </div>
);

export const nodeTypes = { tournamentMatch: MatchNode };
export const edgeTypes = { knockout: KnockoutEdge };

const KnockoutView = () => {
  const graph = KnockoutContext.useSelector((s) => s.context.graph);
  const matchRefs = KnockoutContext.useSelector((s) => s.context.matchRefs);

  const { nodes, edges } = useMemo(() => buildKnockoutView(graph, matchRefs), [graph.nodes.size]);

  return (
    <div style={{ width: "100%", height: "450px", background: theme.colors.bg }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        proOptions={{ hideAttribution: true }}
        fitView
        edgesReconnectable={false}
        nodesDraggable={false}
        minZoom={0}
      />
    </div>
  );
};

export default KnockoutView;
